import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Cloud, Search } from "lucide-react";
import { getWeatherForCity, getWeatherIconUrl, WeatherData } from "@/lib/weather";
import { useToast } from "@/hooks/use-toast";

const WeatherCard = () => {
  const { toast } = useToast();
  const [city, setCity] = useState("");
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchWeather = async () => {
    if (!city.trim()) return;
    
    setLoading(true);
    
    try {
      const data = await getWeatherForCity(city);
      setWeather(data);
    } catch (error) {
      console.error("Error fetching weather:", error);
      toast({
        title: "Error",
        description: "Could not fetch weather data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      fetchWeather();
    }
  };

  return (
    <Card className="shadow-card hover:shadow-card-hover transition-shadow duration-300 overflow-hidden">
      <CardContent className="p-0">
        <div className="p-6 bg-travel-blue text-white">
          <div className="flex items-center space-x-2 mb-4">
            <Cloud className="h-6 w-6" />
            <h2 className="text-xl font-medium">Weather Forecast</h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Enter city name"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyDown={handleKeyDown}
              className="bg-white/10 text-white placeholder:text-white/70 border-white/30 focus-visible:ring-white/30"
            />
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white/20"
              onClick={fetchWeather}
              disabled={loading}
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <div className="w-6 h-6 border-2 border-travel-blue border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : weather ? (
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-medium">{weather.name}</h3>
                <p className="text-3xl font-semibold mt-1">
                  {Math.round(weather.main.temp)}°C
                </p>
                <p className="text-gray-600 text-sm">
                  Feels like {Math.round(weather.main.feels_like)}°C
                </p>
              </div>
              <div className="flex flex-col items-center">
                {weather.weather[0] && (
                  <>
                    <img
                      src={getWeatherIconUrl(weather.weather[0].icon)}
                      alt={weather.weather[0].description}
                      className="w-16 h-16"
                    />
                    <p className="text-gray-600 capitalize">
                      {weather.weather[0].description}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="bg-gray-50 p-2 rounded-md">
                <p className="text-sm text-gray-500">Humidity</p>
                <p className="font-medium">{weather.main.humidity}%</p>
              </div>
              <div className="bg-gray-50 p-2 rounded-md">
                <p className="text-sm text-gray-500">Wind</p>
                <p className="font-medium">{Math.round(weather.wind.speed * 3.6)} km/h</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 flex flex-col items-center justify-center text-gray-500">
            <Cloud className="h-10 w-10 mb-2 opacity-30" />
            <p>Enter a city name to see the current weather</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherCard;
